/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useMemo } from "react";
import {
  FileJson,
  Clock,
  ExternalLink,
  Trash2,
  Search,
  LayoutGrid,
  List,
  Copy,
  Check,
  UploadCloud,
  Menu,
  X,
  Key,
  Plus,
  Terminal,
} from "lucide-react";
import { buildApiUrl } from "../config/api";
import { cn } from "../utils/cn";
import yaml from "js-yaml";
import { OpenAPISpec } from "../types";

interface Spec {
  id: string;
  created_at: string;
  title?: string;
  version?: string;
  endpoint_count?: number;
}

interface ApiKey {
  id: number;
  name: string;
  spec_id: string;
  created_at: string;
  last_used_at: string | null;
}

interface DashboardProps {
  token: string;
  onSpecSelect: (id: string) => void;
  onLogout: () => void;
  user: { username: string; email?: string; avatar?: string };
}

type ViewMode = "grid" | "list";
type SortBy = "newest" | "oldest" | "name";

export function Dashboard({
  token,
  onSpecSelect,
  onLogout,
  user,
}: DashboardProps) {
  const [specs, setSpecs] = useState<Spec[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<SortBy>("newest");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // File upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // API Key management state
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [selectedSpecForApiKey, setSelectedSpecForApiKey] = useState<Spec | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);

  useEffect(() => {
    fetchSpecs();
  }, []);

  const fetchSpecs = async () => {
    try {
      const res = await fetch(buildApiUrl("/api/specs"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const enhancedSpecs = await Promise.all(
          (data.specs || []).map(async (spec: Spec) => {
            try {
              const specRes = await fetch(buildApiUrl(`/api/spec/${spec.id}`));
              if (specRes.ok) {
                const specData = await specRes.json();
                return {
                  ...spec,
                  title: specData.info?.title || "Untitled API",
                  version: specData.info?.version,
                  endpoint_count: Object.values(specData.paths || {}).length,
                };
              }
            } catch {
              // Fallback to basic spec info
            }
            return {
              ...spec,
              title: "API Documentation",
            };
          })
        );
        setSpecs(enhancedSpecs);
      }
    } catch (e) {
      console.error("Failed to fetch specs", e);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (file: File) => {
    setUploadError(null);
    setIsUploading(true);

    try {
      const text = await file.text();
      let parsed: OpenAPISpec;

      if (file.name.endsWith(".yaml") || file.name.endsWith(".yml")) {
        parsed = yaml.load(text) as OpenAPISpec;
      } else {
        parsed = JSON.parse(text) as OpenAPISpec;
      }

      if (!parsed || (!parsed.openapi && !parsed.swagger)) {
        throw new Error("Invalid OpenAPI specification file.");
      }

      // Save to server
      const res = await fetch(buildApiUrl("/api/spec"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(parsed),
      });

      const data = await res.json();
      if (data.success && data.id) {
        const newSpec = {
          id: data.id,
          created_at: new Date().toISOString(),
          title: parsed.info?.title || "Untitled API",
          version: parsed.info?.version,
        };
        setSpecs([newSpec, ...specs]);
        onSpecSelect(data.id);
      } else {
        throw new Error("Failed to save specification");
      }
    } catch (err: any) {
      setUploadError(err.message || "Failed to process file");
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this API documentation?"))
      return;

    setDeleting(id);
    try {
      const res = await fetch(buildApiUrl(`/api/spec/${id}`), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSpecs(specs.filter((s) => s.id !== id));
      }
    } catch (e) {
      console.error("Failed to delete spec", e);
    } finally {
      setDeleting(null);
    }
  };

  const handleCopyLink = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}?id=${id}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredSpecs = useMemo(() => {
    let result = [...specs];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (spec) =>
          spec.title?.toLowerCase().includes(query) ||
          spec.id.toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "oldest":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case "name":
          return (a.title || "").localeCompare(b.title || "");
        default:
          return 0;
      }
    });

    return result;
  }, [specs, searchQuery, sortBy]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // API Key Management Functions
  const openApiKeyModal = async (spec: Spec) => {
    setSelectedSpecForApiKey(spec);
    setApiKeyModalOpen(true);
    setGeneratedKey(null);
    setNewKeyName("");
    await fetchApiKeys();
  };

  const fetchApiKeys = async () => {
    try {
      const res = await fetch(buildApiUrl("/api/api-keys"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setApiKeys(data.keys || []);
      }
    } catch (e) {
      console.error("Failed to fetch API keys", e);
    }
  };

  const generateApiKey = async () => {
    if (!newKeyName.trim() || !selectedSpecForApiKey) return;
    
    setIsGeneratingKey(true);
    try {
      const res = await fetch(buildApiUrl("/api/api-keys"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newKeyName,
          specId: selectedSpecForApiKey.id,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeneratedKey(data.apiKey);
        setNewKeyName("");
        await fetchApiKeys();
      }
    } catch (e) {
      console.error("Failed to generate API key", e);
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const deleteApiKey = async (keyId: number) => {
    try {
      const res = await fetch(buildApiUrl(`/api/api-keys/${keyId}`), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        await fetchApiKeys();
      }
    } catch (e) {
      console.error("Failed to delete API key", e);
    }
  };

  const copyWebhookUrl = (apiKey: string) => {
    const webhookUrl = `${window.location.origin}/api/webhook/update-spec?key=${apiKey}`;
    navigator.clipboard.writeText(webhookUrl);
  };

  const closeApiKeyModal = () => {
    setApiKeyModalOpen(false);
    setSelectedSpecForApiKey(null);
    setGeneratedKey(null);
    setNewKeyName("");
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src="/docgether.png" alt="DocGether" className="h-10 w-auto rounded-lg" />
              <span className="hidden sm:inline text-zinc-400 text-sm">|</span>
              <span className="hidden sm:inline text-xs font-medium text-zinc-500 tracking-wider">DASHBOARD</span>
            </div>

            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a
                href="/documentation"
                className="text-xs font-medium text-zinc-500 hover:text-zinc-900 tracking-wider transition-colors"
              >
                DOCS
              </a>
              <span className="text-sm text-zinc-900">{user.username}</span>
              <button
                onClick={onLogout}
                className="text-xs font-medium text-zinc-500 hover:text-zinc-900 tracking-wider transition-colors"
              >
                SIGN OUT
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-200 px-4 py-4 space-y-4">
            <a
              href="/documentation"
              className="block text-sm font-medium text-zinc-600 w-full text-left"
              onClick={() => setMobileMenuOpen(false)}
            >
              DOCS
            </a>
            <div className="text-sm text-zinc-900">{user.username}</div>
            <button
              onClick={() => { setMobileMenuOpen(false); onLogout(); }}
              className="block text-sm font-medium text-zinc-600 w-full text-left"
            >
              SIGN OUT
            </button>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-10">
            <span className="text-[10px] font-semibold text-zinc-400 tracking-widest mb-2 block">
              WELCOME BACK
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 tracking-tight">
              {user.username}'s Documentation
            </h1>
          </div>

          {/* Upload Section */}
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {/* Upload Box */}
            <div className="lg:col-span-2 border border-zinc-900 p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <span className="text-[10px] font-semibold text-zinc-400 tracking-widest block mb-1">
                    NEW PROJECT
                  </span>
                  <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">
                    Import API Specification
                  </h2>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-zinc-400 tracking-wider">
                  <span>OPENAPI 3.0+</span>
                  <span>SWAGGER 2.0</span>
                  <span>JSON</span>
                  <span>YAML</span>
                </div>
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
                className={cn(
                  "border-2 border-dashed p-8 text-center cursor-pointer transition-all",
                  isDragging
                    ? "border-zinc-900 bg-zinc-50"
                    : "border-zinc-200 hover:border-zinc-400"
                )}
                onClick={() => document.getElementById("dashboard-file-upload")?.click()}
              >
                <input
                  id="dashboard-file-upload"
                  type="file"
                  className="hidden"
                  accept=".json,.yaml,.yml"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                    {isUploading ? (
                      <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent animate-spin" />
                    ) : (
                      <UploadCloud className="w-6 h-6 text-zinc-900" strokeWidth={1.5} />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900 mb-1">
                      {isUploading ? "Uploading..." : "Drop your spec here"}
                    </p>
                    <p className="text-xs text-zinc-500">
                      or click to browse files
                    </p>
                  </div>
                </div>
              </div>

              {uploadError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-600 text-xs">
                  {uploadError}
                </div>
              )}
            </div>

            {/* Stats Box */}
            <div className="border border-zinc-200 p-6 sm:p-8">
              <span className="text-[10px] font-semibold text-zinc-400 tracking-widest block mb-6">
                OVERVIEW
              </span>
              <div className="space-y-6">
                <div>
                  <div className="text-3xl font-bold text-zinc-900">{specs.length}</div>
                  <div className="text-xs text-zinc-500 tracking-wider mt-1">TOTAL DOCUMENTS</div>
                </div>
                <div className="pt-6 border-t border-zinc-100">
                  <div className="text-3xl font-bold text-zinc-900">
                    {specs.filter(s => {
                      const date = new Date(s.created_at);
                      const now = new Date();
                      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
                      return diffDays < 7;
                    }).length}
                  </div>
                  <div className="text-xs text-zinc-500 tracking-wider mt-1">CREATED THIS WEEK</div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 transition-colors"
              />
            </div>

            <div className="flex items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="px-4 py-3 bg-white border border-zinc-200 text-sm text-zinc-700 focus:outline-none focus:border-zinc-900 cursor-pointer"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Name</option>
              </select>

              <div className="flex items-center border border-zinc-200">
                <button
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "p-3 transition-colors",
                    viewMode === "grid"
                      ? "bg-zinc-900 text-white"
                      : "bg-white text-zinc-400 hover:text-zinc-900"
                  )}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "p-3 transition-colors",
                    viewMode === "list"
                      ? "bg-zinc-900 text-white"
                      : "bg-white text-zinc-400 hover:text-zinc-900"
                  )}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Specs Count */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xs font-bold text-zinc-900 tracking-wider">
              YOUR API DOCUMENTATION
            </h2>
            <span className="text-xs text-zinc-500">
              {filteredSpecs.length} {filteredSpecs.length === 1 ? "DOCUMENT" : "DOCUMENTS"}
            </span>
          </div>

          {/* Specs Grid/List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-900 animate-spin" />
            </div>
          ) : filteredSpecs.length === 0 ? (
            <div className="border border-zinc-200 border-dashed p-12 text-center">
              {searchQuery ? (
                <>
                  <Search className="w-8 h-8 text-zinc-300 mx-auto mb-4" />
                  <p className="text-zinc-900 font-medium mb-1">No results found</p>
                  <p className="text-sm text-zinc-500">Try adjusting your search</p>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center mx-auto mb-4">
                    <FileJson className="w-6 h-6 text-zinc-400" />
                  </div>
                  <p className="text-zinc-900 font-medium mb-1">No documents yet</p>
                  <p className="text-sm text-zinc-500">Import your first API specification above</p>
                </>
              )}
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSpecs.map((spec) => (
                <div
                  key={spec.id}
                  onClick={() => onSpecSelect(spec.id)}
                  className="group border border-zinc-200 p-5 hover:border-zinc-900 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center group-hover:bg-zinc-900 group-hover:text-white transition-colors">
                      <FileJson className="w-5 h-5" />
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); openApiKeyModal(spec); }}
                        className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                        title="Manage API Keys"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleCopyLink(spec.id, e)}
                        className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                        title="Copy link"
                      >
                        {copiedId === spec.id ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => handleDelete(spec.id, e)}
                        disabled={deleting === spec.id}
                        className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        {deleting === spec.id ? (
                          <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <h3 className="font-semibold text-zinc-900 mb-1 truncate">
                    {spec.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Clock className="w-3 h-3" />
                    {formatDate(spec.created_at)}
                    {spec.version && (
                      <>
                        <span className="w-1 h-1 bg-zinc-300" />
                        <span>v{spec.version}</span>
                      </>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between">
                    <span className="text-[10px] text-zinc-400 font-mono tracking-wider">
                      {spec.id.slice(0, 8)}
                    </span>
                    <ExternalLink className="w-4 h-4 text-zinc-300 group-hover:text-zinc-900 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-zinc-200">
              {filteredSpecs.map((spec, index) => (
                <div
                  key={spec.id}
                  onClick={() => onSpecSelect(spec.id)}
                  className={cn(
                    "group flex items-center gap-4 p-4 hover:bg-zinc-50 transition-colors cursor-pointer",
                    index !== filteredSpecs.length - 1 && "border-b border-zinc-100"
                  )}
                >
                  <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center group-hover:bg-zinc-900 group-hover:text-white transition-colors">
                    <FileJson className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-zinc-900 truncate">
                      {spec.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <span>{formatDate(spec.created_at)}</span>
                      {spec.version && (
                        <>
                          <span className="w-1 h-1 bg-zinc-300" />
                          <span>v{spec.version}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); openApiKeyModal(spec); }}
                      className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200 transition-colors opacity-0 group-hover:opacity-100"
                      title="Manage API Keys"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => handleCopyLink(spec.id, e)}
                      className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200 transition-colors opacity-0 group-hover:opacity-100"
                      title="Copy link"
                    >
                      {copiedId === spec.id ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={(e) => handleDelete(spec.id, e)}
                      disabled={deleting === spec.id}
                      className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete"
                    >
                      {deleting === spec.id ? (
                        <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                    <ExternalLink className="w-4 h-4 text-zinc-300 ml-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      {/* API Key Management Modal */}
      {apiKeyModalOpen && selectedSpecForApiKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white w-full max-w-lg max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <div>
                <h3 className="font-bold text-zinc-900">CI/CD INTEGRATION</h3>
                <p className="text-xs text-zinc-500 mt-1">{selectedSpecForApiKey.title}</p>
              </div>
              <button
                onClick={closeApiKeyModal}
                className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Info Section */}
              <div className="bg-zinc-50 border border-zinc-200 p-4">
                <div className="flex items-start gap-3">
                  <Terminal className="w-5 h-5 text-zinc-900 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-zinc-900 tracking-wider mb-2">AUTOMATE DOCUMENTATION UPDATES</h4>
                    <p className="text-sm text-zinc-600 leading-relaxed">
                      Generate an API key to automatically update your documentation from your CI/CD pipeline. 
                      Use the webhook URL in your GitHub Actions, GitLab CI, or any other automation tool.
                    </p>
                  </div>
                </div>
              </div>

              {/* Generate New Key */}
              <div>
                <h4 className="text-[10px] font-bold text-zinc-900 tracking-widest mb-3">GENERATE NEW API KEY</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Key name (e.g., Production CI)"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="flex-1 px-4 py-2.5 bg-white border border-zinc-200 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 transition-colors"
                  />
                  <button
                    onClick={generateApiKey}
                    disabled={!newKeyName.trim() || isGeneratingKey}
                    className="px-4 py-2.5 bg-zinc-900 text-white text-xs font-medium tracking-wider hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isGeneratingKey ? (
                      <div className="w-4 h-4 border-2 border-zinc-300 border-t-white animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    GENERATE
                  </button>
                </div>
              </div>

              {/* Generated Key Display */}
              {generatedKey && (
                <div className="border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-emerald-800 tracking-wider">API KEY GENERATED</span>
                    <span className="text-[10px] text-emerald-600">Copy this now - it won&apos;t be shown again</span>
                  </div>
                  <code className="block bg-white border border-emerald-200 p-3 text-xs font-mono text-emerald-900 break-all">
                    {generatedKey}
                  </code>
                  <div className="mt-3 pt-3 border-t border-emerald-200">
                    <span className="text-[10px] font-bold text-emerald-800 tracking-wider block mb-2">WEBHOOK URL</span>
                    <div className="flex gap-2">
                      <code className="flex-1 bg-white border border-emerald-200 p-2 text-[10px] font-mono text-emerald-900 truncate">
                        {`${window.location.origin}/api/webhook/update-spec?key=${generatedKey}`}
                      </code>
                      <button
                        onClick={() => copyWebhookUrl(generatedKey)}
                        className="px-3 py-2 bg-emerald-100 text-emerald-800 text-xs font-medium hover:bg-emerald-200 transition-colors"
                      >
                        COPY
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Keys */}
              <div>
                <h4 className="text-[10px] font-bold text-zinc-900 tracking-widest mb-3">
                  ACTIVE KEYS ({apiKeys.filter(k => k.spec_id === selectedSpecForApiKey.id).length})
                </h4>
                {apiKeys.filter(k => k.spec_id === selectedSpecForApiKey.id).length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-4">No API keys generated yet</p>
                ) : (
                  <div className="space-y-2">
                    {apiKeys
                      .filter(k => k.spec_id === selectedSpecForApiKey.id)
                      .map((key) => (
                        <div key={key.id} className="flex items-center justify-between p-3 border border-zinc-200">
                          <div>
                            <p className="text-sm font-medium text-zinc-900">{key.name}</p>
                            <p className="text-[10px] text-zinc-500">
                              Created {formatDate(key.created_at)}
                              {key.last_used_at && ` • Used ${formatDate(key.last_used_at)}`}
                            </p>
                          </div>
                          <button
                            onClick={() => deleteApiKey(key.id)}
                            className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete API key"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Example Usage */}
              <div className="bg-zinc-900 p-4">
                <h4 className="text-[10px] font-bold text-white tracking-widest mb-3">EXAMPLE: GITHUB ACTIONS</h4>
                <pre className="text-xs text-zinc-400 font-mono overflow-x-auto">
                  <code>{`- name: Update DocGether Documentation
  run: |
    curl -X POST \\
      "${window.location.origin}/api/webhook/update-spec?key=YOUR_API_KEY" \\
      -H "Content-Type: application/json" \\
      -d @openapi.json`}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
